import { graphql, useStaticQuery } from "gatsby"
import PropTypes from "prop-types"
import React, { useState, useEffect, useRef } from "react"
import NoEvents from "components/NoEvents"
import YoutubeModal from "components/YoutubeModal"
import ZoomRedirectModal from "components/ZoomRedirectModal"
import useWebinars from "effects/useWebinars"
import validateZoomUrl from "lib/validateZoomUrl"
import { richTextToHtml } from "utils/richTextToHtml"
import * as css from "./DemoSection.module.css"
import RegisterViaZoomButton from "./RegisterViaZoomButton"
import Panel from "components/Blocks/DemoSection/Panel"
import RecordedDemoButton from "components/Blocks/DemoSection/RecordedDemoButton"
import ScheduledDemoGroup from "components/Blocks/DemoSection/ScheduledDemoGroup"
import ScheduledDemoInfo from "components/Blocks/DemoSection/ScheduledDemoInfo"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import Image from "components/ui/Image"

export const query = graphql`
  fragment DemoSection on ContentfulDemoSection {
    id
    __typename
    label
    heading
    body {
      raw
    }
    videoUrl
    recordedVideoDate
    footer {
      raw
    }
    footerBackgroundImage {
      gatsbyImageData(quality: 100)
      description
    }
  }
`

const allWebinarsQuery = graphql`
  {
    webinars: allZoomWebinar(sort: { start_time: ASC }) {
      edges {
        node {
          id
          topic
          join_url
          start_time
          timezone
        }
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`

function DemoSection({
  label,
  heading,
  body,
  videoUrl,
  recordedVideoDate,
  handsOnLab,
  footer,
  footerBackgroundImage,
}) {
  const { webinars } = useStaticQuery(allWebinarsQuery)
  const groupDemoWebinars = useWebinars("Preset Group Demo", webinars)
  const handsOnLabWebinars = useWebinars(handsOnLab?.webinarTopic, webinars)

  const [open, setOpen] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState(null)

  const bodyDoc = body?.raw ? JSON.parse(body?.raw) : null
  const footerDoc = footer?.raw ? JSON.parse(footer?.raw) : null

  const footerRef = useRef(null)

  useEffect(() => {
    const links = footerRef.current.querySelectorAll("a")
    links.forEach((link) => {
      if (validateZoomUrl(link.href)) {
        link.addEventListener("click", (e) => {
          e.preventDefault()
          setRedirectUrl(link.href)
        })
      }
    })
  }, [])

  return (
    <>
      <Section padding="extraLight">
        <SimpleHeader
          label={label}
          title={heading}
          subtitle={
            bodyDoc && (
              <p
                dangerouslySetInnerHTML={{
                  __html: richTextToHtml(bodyDoc).replace(/\n/g, `<br />`),
                }}
              />
            )
          }
          dash="vertical"
        />
        <div
          className={`${css.board} ${handsOnLab ? css.boardThreePanels : ""}`}
        >
          <Panel
            label="Watch a"
            title="Recorded Demo"
            description="Prefer to enjoy a recorded demo at your own pace? Watch the video here."
          >
            {videoUrl && (
              <RecordedDemoButton
                date={recordedVideoDate}
                title="Preset Demo"
                onClick={() => setOpen(true)}
              />
            )}
          </Panel>
          <div className={css.divider} />
          <Panel
            label="Register for"
            title="Group Demo"
            description="Join a small group for a guided demo through the Preset app."
          >
            {groupDemoWebinars.length ? (
              <>
                <ScheduledDemoGroup title="Upcoming Live Demos">
                  {groupDemoWebinars.map((webinar) => (
                    <ScheduledDemoInfo
                      date={webinar.start_time}
                      timezone={webinar.timezone}
                      key={webinar.id}
                    />
                  ))}
                </ScheduledDemoGroup>
                <RegisterViaZoomButton
                  onClick={() => setRedirectUrl(groupDemoWebinars[0].join_url)}
                />
              </>
            ) : (
              <NoEvents
                message={
                  <>
                    There are currently no Group Demos on the schedule. Please
                    check back later.
                  </>
                }
                narrow
              />
            )}
          </Panel>
          {handsOnLab && (
            <>
              <div className={css.divider} />
              <Panel
                label={handsOnLab.label || "Register for"}
                title={handsOnLab.title || "Hands-on Lab"}
                description={handsOnLab.description}
              >
                {handsOnLabWebinars?.length ? (
                  <>
                    <ScheduledDemoGroup title="Upcoming Labs">
                      {handsOnLabWebinars.map((webinar) => (
                        <ScheduledDemoInfo
                          date={webinar.start_time}
                          timezone={webinar.timezone}
                          key={webinar.id}
                        />
                      ))}
                    </ScheduledDemoGroup>
                    <RegisterViaZoomButton
                      onClick={() =>
                        setRedirectUrl(handsOnLabWebinars[0].join_url)
                      }
                    />
                  </>
                ) : (
                  <NoEvents
                    message={
                      <>
                        There are currently no Hands-on Labs on the schedule.
                        Please check back later.
                      </>
                    }
                    narrow
                  />
                )}
              </Panel>
            </>
          )}
        </div>
        <div
          ref={footerRef}
          className={css.footer}
          dangerouslySetInnerHTML={{
            __html: richTextToHtml(footerDoc).replace(/\n/g, `<br />`),
          }}
        />
        {open && (
          <YoutubeModal url={videoUrl} closeModal={() => setOpen(false)} />
        )}
        {redirectUrl && (
          <ZoomRedirectModal
            url={redirectUrl}
            closeModal={() => setRedirectUrl(null)}
          />
        )}
      </Section>
      {footerBackgroundImage && (
        <Section className={css.footerBackgroundSection}>
          <div className={css.footerBackgroundImage}>
            <Image src={footerBackgroundImage} />
          </div>
        </Section>
      )}
    </>
  )
}

DemoSection.propTypes = {
  label: PropTypes.string,
  heading: PropTypes.string,
  body: PropTypes.shape({
    raw: PropTypes.string,
  }),
  videoUrl: PropTypes.string,
  recordedVideoDate: PropTypes.string,
  handsOnLab: PropTypes.shape({
    label: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    webinarTopic: PropTypes.string.isRequired,
  }),
  footer: PropTypes.shape({
    raw: PropTypes.string,
  }),
  footerBackgroundImage: PropTypes.shape({}),
}

export default DemoSection
